import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { formatCurrency, STATUS_STYLES } from "../lib/constants";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Skeleton } from "../components/ui/skeleton";
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  ResponsiveContainer, PieChart, Pie, Cell, Legend
} from "recharts";
import {
  TrendingUp, TrendingDown, Users, Wallet, ArrowUpRight, AlertTriangle, ShieldCheck
} from "lucide-react";

const STATUS_HEX = {
  "BAŞLANDI": "#3B82F6",
  "BEKLEMEDE": "#F97316",
  "İPTAL": "#EF4444",
  "TAMAMLANDI": "#22C55E",
};

function Metric({ label, value, sub, icon: Icon, tone = "default", testid }) {
  const toneColor = tone === "up" ? "text-green-600 dark:text-green-400"
    : tone === "down" ? "text-red-600 dark:text-red-400"
    : "text-muted-foreground";
  return (
    <Card className="p-6 hover:-translate-y-[2px] hover:shadow-md transition-transform duration-200" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
        {Icon && <Icon className="h-4 w-4 text-muted-foreground" />}
      </div>
      <div className="mt-3 text-3xl font-display font-semibold tracking-tight">{value}</div>
      {sub && <div className={`mt-2 text-xs ${toneColor}`}>{sub}</div>}
    </Card>
  );
}

export default function DashboardPage() {
  const [data, setData] = useState(null);
  const [range, setRange] = useState("monthly");

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setData(r.data)).catch(() => setData(false));
  }, []);

  if (data === null) {
    return (
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
        {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-32" />)}
      </div>
    );
  }
  if (data === false) return <div className="text-red-500">Veri yüklenemedi.</div>;

  const chartData = range === "weekly" ? data.weekly : data.monthly;
  const statusEntries = Object.entries(data.status_counts);
  const platformEntries = Object.entries(data.platform_counts);

  return (
    <div className="space-y-8">
      {/* Metrics */}
      <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-4">
        <Metric
          label="Toplam Gelir"
          value={formatCurrency(data.totals.income)}
          sub={`${data.totals.customers} müşteri`}
          icon={TrendingUp}
          tone="up"
          testid="metric-income"
        />
        <Metric
          label="Toplam Gider"
          value={formatCurrency(data.totals.expense)}
          sub="Manuel giderler"
          icon={TrendingDown}
          tone="down"
          testid="metric-expense"
        />
        <Metric
          label="Net Kazanç"
          value={formatCurrency(data.totals.profit)}
          sub={data.totals.profit >= 0 ? "Kâr" : "Zarar"}
          icon={Wallet}
          tone={data.totals.profit >= 0 ? "up" : "down"}
          testid="metric-profit"
        />
        <Metric
          label="Toplam Müşteri"
          value={data.totals.customers}
          sub={`Tamamlandı: ${data.status_counts["TAMAMLANDI"] || 0}`}
          icon={Users}
          testid="metric-customers"
        />
      </div>

      {/* Warranty + Status */}
      <div className="grid gap-5 md:grid-cols-3">
        <Card className="p-6" data-testid="warranty-card">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Garanti Durumu</div>
          <div className="mt-4 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-green-500/10 flex items-center justify-center">
                <ShieldCheck className="h-5 w-5 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <div className="text-2xl font-display font-semibold">{data.warranty.active}</div>
                <div className="text-xs text-muted-foreground">Devam Eden</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-red-500/10 flex items-center justify-center">
                <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <div className="text-2xl font-display font-semibold">{data.warranty.expired}</div>
                <div className="text-xs text-muted-foreground">Süresi Dolan</div>
              </div>
            </div>
          </div>
        </Card>

        <Card className="p-6 md:col-span-2" data-testid="status-breakdown">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-4">İş Durumu Dağılımı</div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {statusEntries.map(([k, v]) => {
              const s = STATUS_STYLES[k];
              return (
                <div key={k} className="border border-border rounded-lg p-3 flex items-center gap-3">
                  <span className={`h-2.5 w-2.5 rounded-full ${s.dot}`} />
                  <div>
                    <div className="text-lg font-display font-semibold">{v}</div>
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{k}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      </div>

      {/* Chart */}
      <Card className="p-6" data-testid="income-chart">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Gelir & Gider Grafiği</div>
            <div className="mt-1 text-xl font-display font-semibold tracking-tight">Kazanç Trendi</div>
          </div>
          <div className="inline-flex border border-border rounded-full p-1 text-xs">
            {["weekly", "monthly"].map((r) => (
              <button
                key={r}
                data-testid={`range-${r}`}
                onClick={() => setRange(r)}
                className={`px-3 py-1.5 rounded-full transition-colors duration-200 ${range === r ? "bg-foreground text-background" : "text-muted-foreground"}`}
              >
                {r === "weekly" ? "Haftalık" : "Aylık"}
              </button>
            ))}
          </div>
        </div>
        <div className="h-72 -mx-2">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <XAxis dataKey="label" tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} stroke="hsl(var(--border))" />
              <YAxis tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} stroke="hsl(var(--border))" />
              <Tooltip
                contentStyle={{
                  background: "hsl(var(--card))",
                  border: "1px solid hsl(var(--border))",
                  borderRadius: 8,
                  fontSize: 12,
                }}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line type="monotone" dataKey="income" stroke="#22C55E" strokeWidth={2.5} dot={{ r: 3 }} name="Gelir" />
              <Line type="monotone" dataKey="expense" stroke="#EF4444" strokeWidth={2.5} dot={{ r: 3 }} name="Gider" />
              <Line type="monotone" dataKey="profit" stroke="#3B82F6" strokeWidth={2.5} strokeDasharray="4 4" dot={{ r: 3 }} name="Kâr" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Card>

      {/* Status + Platform charts */}
      <div className="grid gap-5 lg:grid-cols-2">
        <Card className="p-6" data-testid="status-bar-chart">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-4">İş Durumu (Bar)</div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={statusEntries.map(([k, v]) => ({ status: k, count: v }))}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="status" tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 10 }} stroke="hsl(var(--border))" />
                <YAxis tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} stroke="hsl(var(--border))" allowDecimals={false} />
                <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
                <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                  {statusEntries.map(([k], i) => <Cell key={i} fill={STATUS_HEX[k]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-6" data-testid="platform-pie">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-4">Platform Dağılımı</div>
          <div className="h-64">
            {platformEntries.length === 0 ? (
              <div className="h-full flex items-center justify-center text-sm text-muted-foreground">Henüz veri yok</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={platformEntries.map(([k, v]) => ({ name: k, value: v }))} dataKey="value" nameKey="name" outerRadius={80} innerRadius={45} paddingAngle={2}>
                    {platformEntries.map(([, ], i) => (
                      <Cell key={i} fill={["#0A0A0A", "#525252", "#A3A3A3", "#3B82F6", "#F97316"][i % 5]} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}
